import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/authContext';

export const ApiKeysPage = () => {
  const { token } = useContext(AuthContext);
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState([]);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showScopeForm, setShowScopeForm] = useState(false);
  const [newScopeName, setNewScopeName] = useState('');
  const [newScopeDesc, setNewScopeDesc] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const fetchKeys = async () => {
    try {
      const res = await fetch('/api/api-keys', { headers });
      if (!res.ok) throw new Error('Error al cargar');
      const data = await res.json();
      setKeys(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchScopes = async () => {
    try {
      const res = await fetch('/api/scopes', { headers });
      if (!res.ok) throw new Error('Error al cargar scopes');
      const data = await res.json();
      setScopes(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchKeys();
    fetchScopes();
  }, []);

  const toggleScope = (name) => {
    setNewKeyScopes(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (newKeyScopes.length === 0) {
      setError('Seleccioná al menos un scope');
      return;
    }
    setError('');
    setNewKeyResult(null);
    setLoading(true);
    try {
      const res = await fetch('/api/api-keys', {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newKeyName, scopes: newKeyScopes }),
      });
      if (!res.ok) throw new Error('Error al crear');
      const data = await res.json();
      setNewKeyResult(data);
      setNewKeyName('');
      setNewKeyScopes([]);
      setShowForm(false);
      fetchKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('¿Desactivar esta API Key? Los sistemas que la usen dejarán de funcionar.')) return;
    try {
      const res = await fetch(`/api/api-keys/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Error al desactivar');
      fetchKeys();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateScope = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch('/api/scopes', {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newScopeName.trim(), description: newScopeDesc.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Error al crear scope');
      }
      setNewScopeName('');
      setNewScopeDesc('');
      setShowScopeForm(false);
      fetchScopes();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleScopeActivo = async (scope) => {
    try {
      const res = await fetch(`/api/scopes/${scope.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ activo: !scope.activo }),
      });
      if (!res.ok) throw new Error('Error al actualizar scope');
      fetchScopes();
    } catch (err) {
      setError(err.message);
    }
  };

  const activeScopes = scopes.filter(s => s.activo);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">API Keys</h1>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>
      )}

      {/* Sección: Crear API Key */}
      <div className="mb-8">
        <button
          onClick={() => { setShowForm(!showForm); setNewKeyResult(null); }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          {showForm ? 'Cancelar' : '+ Nueva API Key'}
        </button>

        {showForm && (
          <form onSubmit={handleCreate} className="mt-4 p-4 border rounded bg-gray-50 dark:bg-gray-800">
            <label className="block text-sm font-medium mb-1">Nombre del sistema consumidor</label>
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              required
              className="w-full p-2 border rounded text-sm mb-4 dark:bg-gray-700 dark:text-white"
              placeholder="Ej: Sistema de Reportes"
            />

            <label className="block text-sm font-medium mb-2">Scopes</label>
            <div className="space-y-1 mb-4">
              {activeScopes.length === 0 && (
                <p className="text-xs text-gray-400">No hay scopes activos. Creá uno más abajo.</p>
              )}
              {activeScopes.map((s) => (
                <label key={s.name} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newKeyScopes.includes(s.name)}
                    onChange={() => toggleScope(s.name)}
                    className="rounded"
                  />
                  <code className="text-xs font-mono">{s.name}</code>
                  {s.description && <span className="text-xs text-gray-500">— {s.description}</span>}
                </label>
              ))}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50"
            >
              {loading ? 'Creando...' : 'Generar API Key'}
            </button>
          </form>
        )}
      </div>

      {/* Key mostrada 1 vez */}
      {newKeyResult && (
        <div className="mb-6 p-4 border-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 rounded">
          <p className="font-bold text-yellow-800 dark:text-yellow-200 mb-1">⚠️ API Key generada</p>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
            Esta es la única vez que se muestra. No se puede recuperar después.
          </p>
          <div className="bg-white dark:bg-gray-900 p-3 rounded border font-mono text-xs break-all select-all">
            {newKeyResult.apiKey}
          </div>
        </div>
      )}

      {/* Tabla de API Keys */}
      <div className="overflow-x-auto mb-10">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700">
              <th className="p-2 text-left">Nombre</th>
              <th className="p-2 text-left">Prefijo</th>
              <th className="p-2 text-left">Scopes</th>
              <th className="p-2 text-left">Activo</th>
              <th className="p-2 text-left">Creada</th>
              <th className="p-2 text-left">Último uso</th>
              <th className="p-2 text-left">Acción</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan="7" className="p-4 text-center text-gray-400">Sin API Keys</td></tr>
            )}
            {keys.map((k) => (
              <tr key={k.id} className="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="p-2">{k.name}</td>
                <td className="p-2 font-mono text-xs">{k.key_prefix}...</td>
                <td className="p-2 text-xs font-mono">{k.scopes?.join(', ')}</td>
                <td className="p-2">{k.activo ? <span className="text-green-600">Sí</span> : <span className="text-red-600">No</span>}</td>
                <td className="p-2 text-xs">{new Date(k.created_at).toLocaleDateString()}</td>
                <td className="p-2 text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '-'}</td>
                <td className="p-2">
                  {k.activo ? (
                    <button onClick={() => handleDeactivate(k.id)} className="text-xs text-red-600 hover:text-red-800">
                      Desactivar
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sección: Gestión de Scopes */}
      <hr className="my-6" />
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Scopes disponibles</h2>
        <button
          onClick={() => setShowScopeForm(!showScopeForm)}
          className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-xs"
        >
          {showScopeForm ? 'Cancelar' : '+ Nuevo Scope'}
        </button>
      </div>

      {showScopeForm && (
        <form onSubmit={handleCreateScope} className="mb-4 p-4 border rounded bg-gray-50 dark:bg-gray-800">
          <label className="block text-xs font-medium mb-1">Nombre del scope</label>
          <input
            type="text"
            value={newScopeName}
            onChange={(e) => setNewScopeName(e.target.value)}
            required
            className="w-full p-2 border rounded text-sm mb-2 dark:bg-gray-700 dark:text-white"
            placeholder="ej: reportes:read"
          />
          <label className="block text-xs font-medium mb-1">Descripción</label>
          <input
            type="text"
            value={newScopeDesc}
            onChange={(e) => setNewScopeDesc(e.target.value)}
            className="w-full p-2 border rounded text-sm mb-3 dark:bg-gray-700 dark:text-white"
            placeholder="ej: Acceso de lectura a reportes"
          />
          <button type="submit" className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs">
            Crear Scope
          </button>
        </form>
      )}

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-700">
            <th className="p-2 text-left">Scope</th>
            <th className="p-2 text-left">Descripción</th>
            <th className="p-2 text-left">Activo</th>
            <th className="p-2 text-left">Acción</th>
          </tr>
        </thead>
        <tbody>
          {scopes.length === 0 && (
            <tr><td colSpan="4" className="p-4 text-center text-gray-400">Sin scopes</td></tr>
          )}
          {scopes.map((s) => (
            <tr key={s.id} className="border-b dark:border-gray-600">
              <td className="p-2 font-mono text-xs">{s.name}</td>
              <td className="p-2 text-xs text-gray-600 dark:text-gray-400">{s.description || '-'}</td>
              <td className="p-2">{s.activo ? <span className="text-green-600">Sí</span> : <span className="text-red-600">No</span>}</td>
              <td className="p-2">
                <button
                  onClick={() => handleToggleScopeActivo(s)}
                  className={`text-xs ${s.activo ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
                >
                  {s.activo ? 'Desactivar' : 'Activar'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
